import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getPayslipsSummary, clearError, clearSuccess, getEmployees } from '../../../../redux/Slices/authSlice';
import { FaPrint, FaFileExcel, FaEye, FaSearch, FaFilter, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import * as XLSX from 'xlsx';
import PdfPreviewModal from '../../Accounting/Reports/components/PdfPreviewModal';
import DropdownWithSearch from '../../../../shared/components/DropdownWithSearch';

const PayslipsSummary = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { payslipsSummary, isLoading, error, success } = useSelector((state) => state.auth);
  const [summaryYear, setSummaryYear] = useState(searchParams.get('year') ? parseInt(searchParams.get('year')) : new Date().getFullYear());
  const [summaryMonth, setSummaryMonth] = useState(searchParams.get('month') ? parseInt(searchParams.get('month')) : new Date().getMonth() + 1);
  const [showSummary, setShowSummary] = useState(false);
  const pdfContainerRef = React.useRef(null);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [pdfPayload, setPdfPayload] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  // Search & Filter & Sort State
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    employee_id: '',
    min_gross: '',
    max_gross: '',
  });

  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null); // 'asc' | 'desc' | null

  // Employee filter dropdown state
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeePagination, setEmployeePagination] = useState(null);
  const employeeSearchInputRef = useRef(null);

  // PDF Preview and Print states
  const [previewData, setPreviewData] = useState(null);
  const [allSummaryForPdf, setAllSummaryForPdf] = useState([]);
  const [pdfChunks, setPdfChunks] = useState([]);

  useEffect(() => {
    setTitle('payslips.summary.title');
  }, [setTitle]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastSuccessRef.current = { message: success, time: now };
      }
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-US') + ' ' + t('payslips.currency');
  };

  const formatPrintCurrency = (value) => Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + t('payslips.currency');

  // Fetch employees for dropdown filter
  const fetchEmployeesForFilter = useCallback(async (page = employeePage, search = employeeSearchTerm) => {
    try {
      const payload = await dispatch(getEmployees({ page, per_page: 10, search: search || undefined, language: i18n.language })).unwrap();
      const items = Array.isArray(payload?.data?.data) ? payload.data.data : (Array.isArray(payload?.data) ? payload.data : []);
      const meta = payload?.data?.meta || payload?.data?.pagination || null;
      setEmployeeOptions(items);
      setEmployeePagination(meta ? {
        current_page: meta.current_page || 1,
        last_page: meta.last_page || meta.total_pages || 1,
        per_page: meta.per_page || 10,
        total: meta.total || 0
      } : null);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  }, [dispatch, i18n.language, employeePage, employeeSearchTerm]);

  useEffect(() => {
    if (employeeDropdownOpen) {
      fetchEmployeesForFilter(employeePage, employeeSearchTerm);
    }
  }, [employeeDropdownOpen, employeePage, fetchEmployeesForFilter]);

  useEffect(() => {
    if (employeeDropdownOpen) {
      const timer = setTimeout(() => {
        setEmployeePage(1);
        fetchEmployeesForFilter(1, employeeSearchTerm);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [employeeSearchTerm, employeeDropdownOpen]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-dropdown-container]') && !event.target.closest('[data-dropdown-toggle]')) {
        setEmployeeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleGetSummary = async () => {
    if (!summaryYear || !summaryMonth) {
      toast.error(t('payslips.summary.selectYearMonth', { defaultValue: 'يرجى اختيار السنة والشهر' }), { rtl: isRTL });
      return;
    }
    await dispatch(getPayslipsSummary({ year: summaryYear, month: summaryMonth }));
    setShowSummary(true);
  };

  // --- Dynamic local filter & search & sort logic ---
  const filteredPayslipsSummary = useMemo(() => {
    if (!payslipsSummary || !Array.isArray(payslipsSummary)) return [];
    let data = [...payslipsSummary];

    // 1. Search term
    const query = searchTerm.trim().replace(/\s+/g, ' ').toLowerCase();
    if (query) {
      data = data.filter((item) => {
        const empNameAr = (item.employee_name_ar || item.employee?.name_ar || '').toLowerCase();
        const empNameEn = (item.employee_name_en || item.employee?.name_en || '').toLowerCase();
        const empName = (item.employee_name || item.employee?.name || '').toLowerCase();
        const empCode = (item.employee_code || item.employee?.employee_code || '').toLowerCase();
        const grossSalary = item.gross_salary ? String(item.gross_salary) : '';
        const netSalary = item.net_salary ? String(item.net_salary) : '';
        const period = (item.pay_period || '').toLowerCase();

        return empNameAr.includes(query) ||
          empNameEn.includes(query) ||
          empName.includes(query) ||
          empCode.includes(query) ||
          grossSalary.includes(query) ||
          netSalary.includes(query) ||
          period.includes(query);
      });
    }

    // 2. Employee filter
    if (filters.employee_id) {
      data = data.filter(item => {
        const empId = item.employee_id || item.employee?.id;
        return String(empId) === String(filters.employee_id);
      });
    }

    // 3. Min Gross
    if (filters.min_gross) {
      data = data.filter(item => (Number(item.gross_salary) || 0) >= Number(filters.min_gross));
    }

    // 4. Max Gross
    if (filters.max_gross) {
      data = data.filter(item => (Number(item.gross_salary) || 0) <= Number(filters.max_gross));
    }

    // 5. Sorting
    if (sortField && sortOrder) {
      data.sort((a, b) => {
        let aVal = '';
        let bVal = '';

        if (sortField === 'employee') {
          aVal = isRTL ? (a.employee_name_ar || a.employee?.name_ar || '') : (a.employee_name_en || a.employee?.name_en || '');
          bVal = isRTL ? (b.employee_name_ar || b.employee?.name_ar || '') : (b.employee_name_en || b.employee?.name_en || '');
        } else if (sortField === 'period') {
          aVal = a.pay_period || '';
          bVal = b.pay_period || '';
        } else if (sortField === 'gross_salary') {
          const aNum = Number(a.gross_salary) || 0;
          const bNum = Number(b.gross_salary) || 0;
          return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
        } else if (sortField === 'net_salary') {
          const aNum = Number(a.net_salary) || 0;
          const bNum = Number(b.net_salary) || 0;
          return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
        }

        return sortOrder === 'asc'
          ? aVal.localeCompare(bVal, isRTL ? 'ar' : 'en', { sensitivity: 'base' })
          : bVal.localeCompare(aVal, isRTL ? 'ar' : 'en', { sensitivity: 'base' });
      });
    }

    return data;
  }, [payslipsSummary, searchTerm, filters, sortField, sortOrder, isRTL]);

  // Reset page when filters change
  useEffect(() => {
    setLocalCurrentPage(1);
  }, [searchTerm, filters]);

  const paginatedPayslips = useMemo(() => {
    return filteredPayslipsSummary.slice((localCurrentPage - 1) * perPage, localCurrentPage * perPage);
  }, [filteredPayslipsSummary, localCurrentPage, perPage]);

  const totalPages = Math.max(1, Math.ceil(filteredPayslipsSummary.length / perPage));

  const handleApplyPerPage = () => {
    const validValue = Math.max(1, Math.min(perPageInput, filteredPayslipsSummary?.length || 1000));
    setPerPage(validValue);
    setLocalCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField !== field) {
      setSortField(field);
      setSortOrder('asc');
    } else if (sortOrder === 'asc') {
      setSortOrder('desc');
    } else {
      setSortField(null);
      setSortOrder(null);
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <span style={{ color: '#666', marginInlineStart: '6px', fontSize: '12px' }}>⇅</span>;
    if (sortOrder === 'asc') return <span style={{ color: '#AC2000', marginInlineStart: '6px', fontSize: '12px' }}>▲</span>;
    return <span style={{ color: '#AC2000', marginInlineStart: '6px', fontSize: '12px' }}>▼</span>;
  };

  // --- Dynamic PDF Pagination ---
  const calculateDynamicChunks = useCallback((list) => {
    const container = document.getElementById('payslips-summary-measurement-container');
    if (!container) {
      const chunks = [];
      for (let i = 0; i < list.length; i += 20) chunks.push(list.slice(i, i + 20));
      return chunks.length > 0 ? chunks : [list];
    }
    const tbody = container.querySelector('tbody');
    if (!tbody) {
      const chunks = [];
      for (let i = 0; i < list.length; i += 20) chunks.push(list.slice(i, i + 20));
      return chunks.length > 0 ? chunks : [list];
    }

    const maxHeight = 700; // px A4 constraint
    const chunks = [];
    let currentChunk = [];
    let currentHeight = 0;

    const rows = tbody.querySelectorAll('tr');
    for (let i = 0; i < rows.length && i < list.length; i++) {
      const rowHeight = rows[i].offsetHeight || 38;
      if (currentHeight + rowHeight > maxHeight && currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = [list[i]];
        currentHeight = rowHeight;
      } else {
        currentChunk.push(list[i]);
        currentHeight += rowHeight;
      }
    }
    if (currentChunk.length > 0) chunks.push(currentChunk);
    return chunks.length > 0 ? chunks : [list];
  }, []);

  const renderPdfPage = (chunk, chunkIdx, totalChunks, elementId = null) => {
    const hasActiveFilters = searchTerm.trim() || filters.employee_id || filters.min_gross || filters.max_gross;

    return (
      <div
        id={elementId}
        style={{ width: '794px', height: '1123px', backgroundColor: '#ffffff', color: '#000000', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', fontWeight: 'bold', padding: '40px', boxSizing: 'border-box', overflow: 'hidden' }}
      >
        <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', height: '1043px', overflow: 'hidden' }}>
          <div style={{ border: '2px solid #000', height: '1033px', padding: '18px 18px 10px', position: 'relative', display: 'flex', flexDirection: 'column', direction: isRTL ? 'rtl' : 'ltr', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: `url(${headerLogo})`, backgroundSize: '60%', backgroundPosition: 'center top', backgroundRepeat: 'no-repeat', opacity: 0.05, zIndex: 0, pointerEvents: 'none' }} />
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', display: 'block', flexShrink: 0 }} crossOrigin="anonymous" />
              <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', flexShrink: 0 }}>
                <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>{t('payslips.summary.title', { defaultValue: 'ملخص الرواتب' })}</div>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>
                  {isRTL ? 'تاريخ التقرير' : 'Report Date'}: {new Date().toLocaleDateString('en-US')} — {isRTL ? 'صفحة' : 'Page'} {chunkIdx + 1} / {totalChunks}
                </div>
                {hasActiveFilters && (
                  <div style={{ fontSize: '9px', color: '#444', marginTop: '6px', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', backgroundColor: 'rgba(238,242,247,0.5)', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                    {searchTerm.trim() && <span>{isRTL ? 'بحث' : 'Search'}: {searchTerm.trim()}</span>}
                    {filters.employee_id && <span>{t('payslips.table.employee')}: {(isRTL ? employeeOptions.find((e) => String(e.id) === String(filters.employee_id))?.name_ar : employeeOptions.find((e) => String(e.id) === String(filters.employee_id))?.name_en) || employeeOptions.find((e) => String(e.id) === String(filters.employee_id))?.name || filters.employee_id}</span>}
                    {filters.min_gross && <span>{isRTL ? 'الحد الأدنى' : 'Min Gross'}: {filters.min_gross}</span>}
                    {filters.max_gross && <span>{isRTL ? 'الحد الأقصى' : 'Max Gross'}: {filters.max_gross}</span>}
                  </div>
                )}
              </div>
              <div style={{ marginTop: '10px', direction: isRTL ? 'rtl' : 'ltr', flex: 1, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', fontFamily: '"Times New Roman", serif', backgroundColor: 'rgba(255,255,255,0.7)', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '37%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                  </colgroup>
                  <thead>
                    <tr style={{ backgroundColor: '#e3f2fd', color: '#0d2a50' }}>
                      <th style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '11px', textAlign: 'center' }}>{t('common.serialNumber', { defaultValue: '#' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '11px', textAlign: isRTL ? 'right' : 'left' }}>{t('payslips.table.employee')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '11px', textAlign: 'center' }}>{t('payslips.table.payPeriod')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '11px', textAlign: 'center' }}>{t('payslips.table.grossSalary')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '11px', textAlign: 'center' }}>{t('payslips.table.netSalary')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chunk.map((item, index) => {
                      const globalIdx = allSummaryForPdf.indexOf(item) + 1;
                      return (
                        <tr key={index}>
                          <td style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '10px', textAlign: 'center', color: '#000' }}>{globalIdx}</td>
                          <td style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '10px', textAlign: isRTL ? 'right' : 'left', color: '#000', wordBreak: 'break-all', whiteSpace: 'normal' }}>
                            {(isRTL ? item.employee_name_ar : item.employee_name_en) || item.employee_name || (isRTL ? item.employee?.name_ar : item.employee?.name_en) || item.employee?.name || '—'}
                          </td>
                          <td style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '10px', textAlign: 'center', color: '#000' }}>{item.pay_period || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '10px', textAlign: 'center', color: '#000', fontWeight: 'bold' }}>{formatPrintCurrency(item.gross_salary)}</td>
                          <td style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '10px', textAlign: 'center', color: '#000', fontWeight: 'bold' }}>{formatPrintCurrency(item.net_salary)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px', flexShrink: 0 }}>
                <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: '100%', height: 'auto', objectFit: 'contain', borderTop: '4px double #000', paddingTop: '8px', display: 'block' }} crossOrigin="anonymous" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handlePreviewAll = () => {
    const list = filteredPayslipsSummary;
    if (list.length === 0) {
      toast.error(t('payslips.summary.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    setAllSummaryForPdf(list);
    requestAnimationFrame(() => {
      setTimeout(() => {
        const chunks = calculateDynamicChunks(list);
        setPdfChunks(chunks);
        setPdfType('summary');
        setPreviewData({ type: 'all', data: list, chunks });
        setIsPreviewOpen(true);
      }, 200);
    });
  };

  const handlePrintAll = async () => {
    const list = filteredPayslipsSummary;
    if (!list || list.length === 0) {
      toast.error(t('payslips.print.noData', { defaultValue: isRTL ? 'لا توجد بيانات للطباعة' : 'No data to print' }), { rtl: isRTL });
      return;
    }
    setAllSummaryForPdf(list);
    await new Promise((resolve) => setTimeout(resolve, 300));
    const chunks = calculateDynamicChunks(list);
    setPdfChunks(chunks);
    setPdfType('summary');
    await new Promise((resolve) => setTimeout(resolve, 600));
    setIsGeneratingPdf(true);

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();

      for (let i = 0; i < chunks.length; i++) {
        const element = document.getElementById(`payslips-summary-pdf-all-${i}`);
        if (!element) continue;
        const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false, backgroundColor: "#ffffff" });
        const imgData = canvas.toDataURL("image/png", 1.0);
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight, undefined, "FAST");
      }
      pdf.save(`Payslips_Summary_${summaryYear}_${summaryMonth}.pdf`);
      toast.success(t('payslips.print.success', { defaultValue: 'تم تنزيل PDF بنجاح' }), { rtl: isRTL });
    } catch (err) {
      console.error(err);
      toast.error(t('payslips.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء ملف كشف الرواتب' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
      setAllSummaryForPdf([]);
    }
  };

  const handleExportToExcel = () => {
    const list = filteredPayslipsSummary;
    if (list.length === 0) {
      toast.error(t('payslips.summary.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    const data = list.map((item, index) => ({
      [t('common.serialNumber', { defaultValue: isRTL ? 'م' : '#' })]: index + 1,
      [t('payslips.table.employee', { defaultValue: isRTL ? 'الموظف' : 'Employee' })]: (isRTL ? item.employee_name_ar : item.employee_name_en) || item.employee_name || (isRTL ? item.employee?.name_ar : item.employee?.name_en) || item.employee?.name || '—',
      [t('payslips.table.code', { defaultValue: isRTL ? 'الكود' : 'Code' })]: item.employee_code || item.employee?.employee_code || '—',
      [t('payslips.table.grossSalary', { defaultValue: isRTL ? 'الراتب الإجمالي' : 'Gross Salary' })]: item.gross_salary || 0,
      [t('payslips.table.netSalary', { defaultValue: isRTL ? 'الراتب الصافي' : 'Net Salary' })]: item.net_salary || 0,
      [t('payslips.table.payPeriod', { defaultValue: isRTL ? 'الفترة' : 'Pay Period' })]: item.pay_period || '—'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('payslips.summary.title', { defaultValue: isRTL ? 'ملخص الرواتب' : 'Payslips Summary' }));
    const fileName = `${t('payslips.summary.title', { defaultValue: isRTL ? 'ملخص_الرواتب' : 'Payslips_Summary' })}_${summaryYear}_${summaryMonth}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('payslips.summary.exportSuccess', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
  };

  const months = [
    { value: 1, label: t('payslips.months.1', { defaultValue: isRTL ? 'يناير' : 'January' }) },
    { value: 2, label: t('payslips.months.2', { defaultValue: isRTL ? 'فبراير' : 'February' }) },
    { value: 3, label: t('payslips.months.3', { defaultValue: isRTL ? 'مارس' : 'March' }) },
    { value: 4, label: t('payslips.months.4', { defaultValue: isRTL ? 'أبريل' : 'April' }) },
    { value: 5, label: t('payslips.months.5', { defaultValue: isRTL ? 'مايو' : 'May' }) },
    { value: 6, label: t('payslips.months.6', { defaultValue: isRTL ? 'يونيو' : 'June' }) },
    { value: 7, label: t('payslips.months.7', { defaultValue: isRTL ? 'يوليو' : 'July' }) },
    { value: 8, label: t('payslips.months.8', { defaultValue: isRTL ? 'أغسطس' : 'August' }) },
    { value: 9, label: t('payslips.months.9', { defaultValue: isRTL ? 'سبتمبر' : 'September' }) },
    { value: 10, label: t('payslips.months.10', { defaultValue: isRTL ? 'أكتوبر' : 'October' }) },
    { value: 11, label: t('payslips.months.11', { defaultValue: isRTL ? 'نوفمبر' : 'November' }) },
    { value: 12, label: t('payslips.months.12', { defaultValue: isRTL ? 'ديسمبر' : 'December' }) }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir
    }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'white',
          marginBottom: '20px',
          textAlign
        }}>
          {t('payslips.summary.title', { defaultValue: 'ملخص الرواتب' })}
        </h1>

        {/* Period Selection Form */}
        <div style={{
          backgroundColor: '#1b3350',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          border: '1px solid #333'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', color: 'white', marginBottom: '15px', textAlign }}>
            {t('payslips.summary.selectPeriod', { defaultValue: isRTL ? 'اختر الفترة' : 'Select Period' })}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#ccc', textAlign, direction: dir }}>
                {t('payslips.summary.year', { defaultValue: isRTL ? 'السنة' : 'Year' })}
              </label>
              <input
                type="number"
                value={summaryYear}
                onChange={(e) => { setSummaryYear(parseInt(e.target.value) || new Date().getFullYear()); setShowSummary(false); }}
                min="2000"
                max="2100"
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px'
                }}
                placeholder={new Date().getFullYear().toString()}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#ccc', textAlign, direction: dir }}>
                {t('payslips.summary.month', { defaultValue: isRTL ? 'الشهر' : 'Month' })}
              </label>
              <select
                value={summaryMonth}
                onChange={(e) => { setSummaryMonth(parseInt(e.target.value)); setShowSummary(false); }}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px'
                }}
              >
                {months.map(month => (
                  <option key={month.value} value={month.value} style={{ backgroundColor: '#1a1f2e' }}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <button
                onClick={handleGetSummary}
                disabled={isLoading}
                style={{
                  width: '100%',
                  backgroundColor: isLoading ? '#666' : '#AC2000',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.7 : 1
                }}
              >
                {isLoading ? t('payslips.loading', { defaultValue: isRTL ? 'جاري التحميل...' : 'Loading...' }) : t('payslips.summary.getSummary', { defaultValue: isRTL ? 'عرض الملخص' : 'View Summary' })}
              </button>
            </div>
          </div>
        </div>

        {/* Display Summary Container */}
        {showSummary && payslipsSummary && (
          <>
            {/* Search & Collapsible Filters Toggle */}
            <div style={{ backgroundColor: "#1a1f2e", padding: "20px", borderRadius: "12px", border: "1px solid #333", marginBottom: "20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px" }}>
                <div style={{ position: "relative", flex: "1", minWidth: "280px", maxWidth: "500px" }}>
                  <FaSearch style={{ position: "absolute", top: "50%", left: isRTL ? "auto" : "15px", right: isRTL ? "15px" : "auto", transform: "translateY(-50%)", color: "#666" }} />
                  <input
                    type="text"
                    placeholder={t("payslips.summary.searchPlaceholder", { defaultValue: isRTL ? "البحث عن موظف، كود، مبلغ..." : "Search for employee, code, amount..." })}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: "100%", padding: isRTL ? "12px 45px 12px 15px" : "12px 15px 12px 45px", backgroundColor: "#14263b", border: "1px solid #333", borderRadius: "8px", color: "white", outline: "none", fontSize: "14px", boxSizing: "border-box" }}
                  />
                </div>
                <button
                  onClick={() => setIsFilterOpen((o) => !o)}
                  style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 18px", backgroundColor: isFilterOpen ? "#AC2000" : "#2d3748", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}
                >
                  <FaFilter /> {isRTL ? "الفلاتر" : "Filters"}
                </button>
              </div>

              {/* Collapsible Filter Panel */}
              {isFilterOpen && (
                <div style={{ marginTop: "20px", paddingTop: "20px", borderTop: "1px solid #333" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "20px", marginBottom: "20px" }}>

                    {/* Employee Filter */}
                    <div>
                      <label style={{ display: "block", marginBottom: "8px", color: "#ccc", fontSize: "14px", fontWeight: "500", textAlign: isRTL ? "right" : "left" }}>
                        {t("payslips.table.employee", { defaultValue: 'الموظف' })}
                      </label>
                      <div style={{ position: "relative" }} data-dropdown-container="true">
                        <div
                          data-dropdown-toggle="true"
                          onClick={() => setEmployeeDropdownOpen((o) => !o)}
                          style={{ width: "100%", padding: "12px 15px", backgroundColor: "#14263b", border: "1px solid #333", borderRadius: "8px", color: filters.employee_id ? "white" : "#666", fontSize: "14px", minHeight: "45px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", boxSizing: "border-box" }}
                        >
                          <span>
                            {filters.employee_id
                              ? employeeOptions.find((e) => String(e.id) === String(filters.employee_id))?.name || (isRTL ? "موظف محدد" : "Selected")
                              : (isRTL ? "جميع الموظفين" : "All Employees")}
                          </span>
                          {filters.employee_id ? (
                            <span onClick={(e) => { e.stopPropagation(); setFilters(prev => ({ ...prev, employee_id: '' })); setEmployeeDropdownOpen(false); }} style={{ cursor: "pointer", color: "#dc3545", fontSize: "12px", padding: "2px 4px" }}>✕</span>
                          ) : (
                            <span style={{ fontSize: 12 }}>▼</span>
                          )}
                        </div>
                        {employeeDropdownOpen && (
                          <DropdownWithSearch
                            isOpen={employeeDropdownOpen}
                            searchTerm={employeeSearchTerm}
                            onSearchChange={setEmployeeSearchTerm}
                            searchInputRef={employeeSearchInputRef}
                            items={employeeOptions}
                            pagination={employeePagination}
                            currentPage={employeePage}
                            onPageChange={setEmployeePage}
                            onClose={() => setEmployeeDropdownOpen(false)}
                            onSelect={(id) => {
                              setFilters(prev => ({ ...prev, employee_id: String(id) }));
                              setEmployeeDropdownOpen(false);
                              setEmployeeSearchTerm("");
                              setEmployeePage(1);
                            }}
                            label={isRTL ? "الموظف" : "Employee"}
                            isRTL={isRTL}
                            t={t}
                            searchPlaceholder={isRTL ? "ابحث عن موظف..." : "Search for employee..."}
                          />
                        )}
                      </div>
                    </div>

                    {/* Min Gross Filter */}
                    <div>
                      <label style={{ display: "block", marginBottom: "8px", color: "#ccc", fontSize: "14px", fontWeight: "500", textAlign: isRTL ? "right" : "left" }}>
                        {isRTL ? "الحد الأدنى للراتب" : "Min Gross Salary"}
                      </label>
                      <input
                        type="number"
                        value={filters.min_gross}
                        onChange={(e) => setFilters(prev => ({ ...prev, min_gross: e.target.value }))}
                        style={{ width: "100%", padding: "12px 15px", backgroundColor: "#14263b", border: "1px solid #333", borderRadius: "8px", color: "white", fontSize: "14px", minHeight: "45px", outline: "none", boxSizing: "border-box" }}
                        placeholder={isRTL ? "مثال: 5000" : "e.g. 5000"}
                      />
                    </div>

                    {/* Max Gross Filter */}
                    <div>
                      <label style={{ display: "block", marginBottom: "8px", color: "#ccc", fontSize: "14px", fontWeight: "500", textAlign: isRTL ? "right" : "left" }}>
                        {isRTL ? "الحد الأقصى للراتب" : "Max Gross Salary"}
                      </label>
                      <input
                        type="number"
                        value={filters.max_gross}
                        onChange={(e) => setFilters(prev => ({ ...prev, max_gross: e.target.value }))}
                        style={{ width: "100%", padding: "12px 15px", backgroundColor: "#14263b", border: "1px solid #333", borderRadius: "8px", color: "white", fontSize: "14px", minHeight: "45px", outline: "none", boxSizing: "border-box" }}
                        placeholder={isRTL ? "مثال: 15000" : "e.g. 15000"}
                      />
                    </div>

                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                    <button
                      onClick={() => {
                        setFilters({ employee_id: '', min_gross: '', max_gross: '' });
                        setSearchTerm('');
                      }}
                      style={{ padding: "8px 16px", backgroundColor: "#333", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: "bold" }}
                    >
                      {isRTL ? "إعادة ضبط" : "Reset"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* List Operations Summary Panel */}
            <div style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px',
              border: '1px solid #333'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                  <h2 style={{ margin: 0, fontSize: '18px', color: 'white', textAlign }}>
                    {t('payslips.summary.summaryFor', { defaultValue: isRTL ? 'ملخص الرواتب لشهر' : 'Payslips Summary for' })} {months.find(m => m.value === summaryMonth)?.label} {summaryYear}
                  </h2>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  flexDirection: isRTL ? 'row-reverse' : 'row',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <label style={{ color: 'white', fontSize: '14px', whiteSpace: 'nowrap' }}>
                      {t('common.perPage', { defaultValue: 'عدد السجلات' })}:
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <button
                        onClick={() => setPerPageInput(Math.max(1, perPageInput - 1))}
                        style={{
                          backgroundColor: '#1b3350',
                          color: 'white',
                          border: '1px solid #333',
                          padding: '6px 10px',
                          borderRadius: '6px 0 0 6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '32px'
                        }}
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min="1"
                        max={filteredPayslipsSummary?.length || 1000}
                        value={perPageInput}
                        onChange={(e) => setPerPageInput(Math.max(1, parseInt(e.target.value) || 1))}
                        style={{
                          width: '70px',
                          padding: '6px 10px',
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderLeft: 'none',
                          borderRight: 'none',
                          borderRadius: 0,
                          color: 'white',
                          fontSize: '14px',
                          textAlign: 'center'
                        }}
                      />
                      <button
                        onClick={() => setPerPageInput(Math.min(filteredPayslipsSummary?.length || 1000, perPageInput + 1))}
                        style={{
                          backgroundColor: '#1b3350',
                          color: 'white',
                          border: '1px solid #333',
                          padding: '6px 10px',
                          borderRadius: '0 6px 6px 0',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '32px'
                        }}
                      >
                        +
                      </button>
                    </div>
                    <button
                      onClick={handleApplyPerPage}
                      style={{
                        backgroundColor: '#AC2000',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {t('common.apply', { defaultValue: 'تطبيق' })}
                    </button>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginInlineStart: 'auto' }}>
                    <button
                      onClick={handlePreviewAll}
                      style={{
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <FaEye />
                      {t('payslips.summary.preview', { defaultValue: 'عرض PDF' })}
                    </button>
                    <button
                      onClick={handlePrintAll}
                      disabled={isGeneratingPdf}
                      style={{
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        opacity: isGeneratingPdf ? 0.7 : 1
                      }}
                    >
                      <FaPrint />
                      {t('payslips.summary.print', { defaultValue: 'طباعة' })}
                    </button>
                    <button
                      onClick={handleExportToExcel}
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <FaFileExcel />
                      {t('payslips.summary.export', { defaultValue: 'تصدير Excel' })}
                    </button>
                  </div>
                </div>
              </div>

              {/* Data Table */}
              {paginatedPayslips.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#1a1f2e', borderBottom: '2px solid #AC2000' }}>
                        <th style={{ padding: '12px', textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: '14px', borderBottom: '1px solid #333', width: '80px' }}>
                          {t('payslips.table.number', { defaultValue: '#' })}
                        </th>
                        <th
                          onClick={() => handleSort('employee')}
                          style={{ padding: '12px', textAlign: isRTL ? 'right' : 'left', color: 'white', fontWeight: 'bold', fontSize: '14px', borderBottom: '1px solid #333', cursor: 'pointer' }}
                        >
                          {t('payslips.table.employee', { defaultValue: 'الموظف' })} {getSortIcon('employee')}
                        </th>
                        <th
                          onClick={() => handleSort('period')}
                          style={{ padding: '12px', textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: '14px', borderBottom: '1px solid #333', cursor: 'pointer' }}
                        >
                          {t('payslips.table.payPeriod', { defaultValue: 'الفترة' })} {getSortIcon('period')}
                        </th>
                        <th
                          onClick={() => handleSort('gross_salary')}
                          style={{ padding: '12px', textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: '14px', borderBottom: '1px solid #333', cursor: 'pointer' }}
                        >
                          {t('payslips.table.grossSalary', { defaultValue: 'الراتب الإجمالي' })} {getSortIcon('gross_salary')}
                        </th>
                        <th
                          onClick={() => handleSort('net_salary')}
                          style={{ padding: '12px', textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: '14px', borderBottom: '1px solid #333', cursor: 'pointer' }}
                        >
                          {t('payslips.table.netSalary', { defaultValue: 'الراتب الصافي' })} {getSortIcon('net_salary')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPayslips.map((item, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #333' }}>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white' }}>
                            {(localCurrentPage - 1) * perPage + index + 1}
                          </td>
                          <td style={{ padding: '12px', textAlign: isRTL ? 'right' : 'left', color: 'white' }}>
                            <div style={{ fontWeight: '500' }}>
                              {(isRTL ? item.employee_name_ar : item.employee_name_en) || item.employee_name || (isRTL ? item.employee?.name_ar : item.employee?.name_en) || item.employee?.name || '—'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                              {item.employee_code || item.employee?.employee_code || ''}
                            </div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white' }}>
                            {item.pay_period || '—'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>
                            {formatCurrency(item.gross_salary)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>
                            {formatCurrency(item.net_salary)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#ccc' }}>
                  {t('payslips.summary.noData', { defaultValue: 'لا توجد نتائج مطابقة' })}
                </div>
              )}

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div style={{
                  padding: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  borderTop: '1px solid #333',
                  flexWrap: 'wrap',
                  marginTop: '20px'
                }}>
                  <button
                    onClick={() => setLocalCurrentPage(Math.max(1, localCurrentPage - 1))}
                    disabled={localCurrentPage === 1}
                    style={{
                      backgroundColor: localCurrentPage === 1 ? '#333' : '#AC2000',
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      cursor: localCurrentPage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    {t('payslips.pagination.previous', { defaultValue: 'السابق' })}
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setLocalCurrentPage(page)}
                      style={{
                        backgroundColor: page === localCurrentPage ? '#AC2000' : '#1a1f2e',
                        color: 'white',
                        border: '1px solid #333',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: page === localCurrentPage ? 'bold' : 'normal'
                      }}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => setLocalCurrentPage(Math.min(totalPages, localCurrentPage + 1))}
                    disabled={localCurrentPage === totalPages}
                    style={{
                      backgroundColor: localCurrentPage === totalPages ? '#333' : '#AC2000',
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      cursor: localCurrentPage === totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    {t('payslips.pagination.next', { defaultValue: 'التالي' })}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Dynamic PDF Measurement Container */}
      <div id="payslips-summary-measurement-container" style={{ position: 'absolute', top: '-99999px', left: '-99999px', width: '714px', visibility: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {filteredPayslipsSummary.map((item, index) => (
              <tr key={index} style={{ borderBottom: '1px solid #000', height: '38px' }}>
                <td style={{ padding: '8px', fontSize: '12px' }}>{index + 1}</td>
                <td style={{ padding: '8px', fontSize: '12px' }}>{(isRTL ? item.employee_name_ar : item.employee_name_en) || item.employee_name || '—'}</td>
                <td style={{ padding: '8px', fontSize: '12px' }}>{item.pay_period || '—'}</td>
                <td style={{ padding: '8px', fontSize: '12px' }}>{formatPrintCurrency(item.gross_salary)}</td>
                <td style={{ padding: '8px', fontSize: '12px' }}>{formatPrintCurrency(item.net_salary)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PDF Chunks rendering in hidden container */}
      {pdfType === 'summary' && pdfChunks && pdfChunks.length > 0 && (
        <div style={{ position: 'absolute', top: '-99999px', left: '-99999px' }}>
          {pdfChunks.map((chunk, index) => (
            <React.Fragment key={index}>
              {renderPdfPage(chunk, index, pdfChunks.length, `payslips-summary-pdf-all-${index}`)}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* PDF Preview Modal */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && !!previewData}
        title={t('payslips.summary.title', { defaultValue: 'ملخص الرواتب' })}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewData(null);
          setPdfChunks([]);
          setPdfType(null);
        }}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
      >
        {previewData?.chunks?.map((chunk, i) => (
          <React.Fragment key={i}>
            {renderPdfPage(chunk, i, previewData.chunks.length, null)}
          </React.Fragment>
        ))}
      </PdfPreviewModal>
    </div>
  );
};

export default PayslipsSummary;
